import type { Company, ToolsTimeline, ToolsTimelineYear } from '../@types/Company';

const MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

/**
 * Calendar year of a planning event, given the year the cycle starts in.
 * The planning cycle runs from `startMonth` to `startMonth - 1`, so every
 * month before the start month belongs to the following calendar year
 * (e.g. with a September start, "Référent 2" mi-janvier of the 2026 cycle
 * happens in January 2027).
 *
 * Mirrors `TimelineEventResolver::dateFromPosition()` on the backend.
 */
export function calendarYearForMonth(month: number, startMonth: number, cycleYear: number): number {
  return month < startMonth ? cycleYear + 1 : cycleYear;
}

/**
 * The planning cycle in progress, which is the key to read in `toolsTimeline`.
 * A cycle started last year keeps running until its start month comes back
 * (with a September start, the 2026 cycle runs until August 2027). When no saved
 * cycle covers `now`, the answer is the current year: a planning saved ahead of
 * its start month, or the key of the planning to create.
 *
 * Mirrors `TimelineEventResolver::currentCycleYear()` on the backend.
 */
export function currentPlanningCycleYear(toolsTimeline: ToolsTimeline | null | undefined, now: Date = new Date()): number {
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  for (const cycleYear of [year, year - 1]) {
    const yearData = toolsTimeline?.[String(cycleYear)];
    if (yearData && calendarYearForMonth(month, yearData.startMonth ?? 9, cycleYear) === year) {
      return cycleYear;
    }
  }
  return year;
}

/**
 * Human-friendly label with spacing convention used in UI:
 * - "début <mois>", "mi-<mois>", "fin <mois>"
 */
export function labelFromPositionHuman(startMonth: number, position: number): string {
  const monthIndexFromStart = Math.floor(position);
  const fraction = position - monthIndexFromStart;
  const actualMonth = ((startMonth - 1 + monthIndexFromStart) % 12);
  const monthName = MONTHS[actualMonth];
  if (fraction < 0.33) { return `début ${monthName}`; }
  if (fraction > 0.66) { return `fin ${monthName}`; }
  return `mi-${monthName}`;
}

/**
 * Inverse of the (startMonth, position) → calendar slot mapping used in the
 * planning module. Given an exact date {day, month} and a launch month,
 * returns the fractional position whose début/mi/fin band contains that day.
 *
 * Mirrors the day → fraction convention used in
 * {@see labelFromPositionHuman} (5 / 15 / 25 anchors → début / mi / fin).
 */
export function positionFromExactDate(startMonth: number, day: number, month: number): number {
  const monthOffset = (((month - startMonth) % 12) + 12) % 12;
  let fraction: number;
  if (day <= 10) {
    fraction = 0.16;
  } else if (day <= 20) {
    fraction = 0.5;
  } else {
    fraction = 0.83;
  }
  const position = monthOffset + fraction;
  return Math.max(0, Math.min(11.5, position));
}

/**
 * Compute the Tuesday closest to the calendar slot represented by
 * (startMonth, position) for the given reference year. The slot anchor day is
 * 5 / 15 / 25 depending on the fractional part of the position (matching the
 * existing début/mi/fin convention). The resulting Tuesday always lands within
 * the same calendar month (anchor ± 3 days max), so no month or year drift can
 * occur.
 */
export function closestTuesdayForPosition(
  startMonth: number,
  position: number,
  currentYear: number,
): { day: number; month: number } {
  const monthOffset = Math.floor(position);
  const fraction = position - monthOffset;
  const actualMonth = ((startMonth - 1 + monthOffset) % 12);
  const yearOffset = Math.floor((startMonth - 1 + monthOffset) / 12);
  const year = currentYear + yearOffset;
  let anchorDay: number;
  if (fraction < 0.33) {
    anchorDay = 5;
  } else if (fraction > 0.66) {
    anchorDay = 25;
  } else {
    anchorDay = 15;
  }
  const reference = new Date(year, actualMonth, anchorDay);
  const dayOfWeek = reference.getDay();
  let diff = (2 - dayOfWeek + 7) % 7;
  if (diff > 3) {
    diff -= 7;
  }
  reference.setDate(anchorDay + diff);
  return { day: reference.getDate(), month: reference.getMonth() + 1 };
}

/**
 * Resolve boitage activation for a given year, using the same fallback policy as dates:
 * - Try requested year, then nearest past years, then nearest available year.
 */
export function resolveBoitageActivation(company: Company | null | undefined, year: number): { 'Boîtage 1': boolean; 'Boîtage 2': boolean } | null {
  const timeline = company?.toolsTimeline || null;
  if (!timeline) { return null; }

  const years = Object.keys(timeline)
    .map((k) => Number(k))
    .filter((n) => !Number.isNaN(n))
    .sort((a, b) => a - b);

  const candidates: number[] = [];
  if (years.includes(year)) { candidates.push(year); }
  const past = years.filter((y) => y < year).sort((a, b) => b - a);
  const future = years.filter((y) => y > year).sort((a, b) => a - b);
  for (const y of past) { candidates.push(y); }
  for (const y of future) { candidates.push(y); }

  for (const y of candidates) {
    const yd: ToolsTimelineYear | null = timeline[String(y)] ?? null;
    if (!yd || typeof yd !== 'object') { continue; }
    const eventActivation = yd.eventActivation;
    if (!eventActivation || typeof eventActivation !== 'object') { continue; }
    const boitage = eventActivation.boitage;
    if (!boitage || typeof boitage !== 'object') { continue; }
    if (Object.prototype.hasOwnProperty.call(boitage, 'Boîtage 1') && Object.prototype.hasOwnProperty.call(boitage, 'Boîtage 2')) {
      return {
        'Boîtage 1': Boolean(boitage['Boîtage 1']),
        'Boîtage 2': Boolean(boitage['Boîtage 2']),
      };
    }
  }

  return null;
}

/**
 * Resolve the visibility flag of any planning row (e.g. "boitage",
 * "referent-mails", "sci-mails", "ag-communication") for the given year,
 * using the same fallback policy as dates: try the requested year first,
 * then nearest past years, then nearest future years. Defaults to true
 * (visible) when nothing is configured.
 *
 * Mirrors {@see TimelineEventResolver::resolveRowVisibility} on the
 * backend.
 */
export function resolveTimelineRowVisibility(
  company: Company | null | undefined,
  year: number,
  rowKey: string,
): boolean {
  const timeline = company?.toolsTimeline || null;
  if (!timeline) { return true; }

  const years = Object.keys(timeline)
    .map((k) => Number(k))
    .filter((n) => !Number.isNaN(n))
    .sort((a, b) => a - b);

  const candidates: number[] = [];
  if (years.includes(year)) { candidates.push(year); }
  const past = years.filter((y) => y < year).sort((a, b) => b - a);
  const future = years.filter((y) => y > year).sort((a, b) => a - b);
  for (const y of past) { candidates.push(y); }
  for (const y of future) { candidates.push(y); }

  for (const y of candidates) {
    const yd: ToolsTimelineYear | null = timeline[String(y)] ?? null;
    if (!yd || typeof yd !== 'object') { continue; }
    const rowVisibility = yd.rowVisibility;
    if (!rowVisibility || typeof rowVisibility !== 'object') { continue; }
    if (Object.prototype.hasOwnProperty.call(rowVisibility, rowKey)) {
      return Boolean(rowVisibility[rowKey]);
    }
  }

  return true;
}

/**
 * Resolve boitage row visibility for a given year. Thin wrapper around
 * {@see resolveTimelineRowVisibility} kept for the existing callers.
 */
export function resolveBoitageRowVisibility(company: Company | null | undefined, year: number): boolean {
  return resolveTimelineRowVisibility(company, year, 'boitage');
}

